import { ReviewingParty } from "@redotech/congo-enums/reviewing-party";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { Big } from "big.js";
import { DraftReturn } from "./draft-return";
import { getPrimaryProduct, getReturnableItemTotalReturnValue } from "./util";

const CLAIM_DOCUMENTS_VALUE_THRESHOLD_USD = Big(1000);

/**
 * Answer codes whose claims always require a police report: "stolen" =
 * Shipsurance's "Loss after Delivery" column.
 */
export const POLICE_REPORT_REASON_CODES = ["stolen"] as const;

/**
 * Answer codes requiring a police report only when the carrier marked the
 * package delivered: Shipsurance treats those claims as potential theft.
 */
export const DELIVERED_POLICE_REPORT_REASON_CODES = ["not_received"] as const;

/** Front and back of the government ID. */
export const REQUIRED_ID_DOCUMENT_COUNT = 2;

export function hasRequiredClaimDocuments({
  idDocumentCount,
  supportingDocumentCount,
  policeReportRequired,
}: {
  idDocumentCount: number;
  supportingDocumentCount: number;
  policeReportRequired: boolean;
}): boolean {
  return (
    idDocumentCount >= REQUIRED_ID_DOCUMENT_COUNT &&
    (!policeReportRequired || supportingDocumentCount > 0)
  );
}

export function shouldCollectPoliceReport(draftReturn: DraftReturn): boolean {
  return draftReturn.pendingReturnItems.some((pendingItem) => {
    if (
      pendingItem.reasonCodes.some((code) =>
        POLICE_REPORT_REASON_CODES.includes(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          code as (typeof POLICE_REPORT_REASON_CODES)[number],
        ),
      )
    ) {
      return true;
    }
    if (
      !pendingItem.reasonCodes.some((code) =>
        DELIVERED_POLICE_REPORT_REASON_CODES.includes(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          code as (typeof DELIVERED_POLICE_REPORT_REASON_CODES)[number],
        ),
      )
    ) {
      return false;
    }
    const returnableItem = draftReturn.returnableItems.find(
      (item) => item.id === pendingItem.returnableItemId,
    );
    return (
      !!returnableItem && getPrimaryProduct(returnableItem).isDelivered === true
    );
  });
}

/**
 * Redo-reviewed Package Protection Plus claims worth at least $1,000 collect a
 * government ID and supporting documentation once per return.
 */
export function shouldCollectReturnLevelClaimDocuments({
  draftReturn,
  packageProtectionPlusEnabled,
  ppIdCollectionEnabled,
}: {
  draftReturn: DraftReturn;
  packageProtectionPlusEnabled: boolean;
  ppIdCollectionEnabled: boolean;
}): boolean {
  if (!ppIdCollectionEnabled) {
    return false;
  }
  if (!packageProtectionPlusEnabled) {
    return false;
  }
  if (draftReturn.returnType !== ReturnTypeEnum.CLAIM) {
    return false;
  }
  if (draftReturn.reviewingParty !== ReviewingParty.Redo) {
    return false;
  }
  if (!draftReturn.orders.some((order) => order.packageProtected)) {
    return false;
  }

  const selectedItemsValuePresentment = draftReturn.pendingReturnItems.reduce(
    (total, pendingItem) => {
      const returnableItem = draftReturn.returnableItems.find(
        (item) => item.id === pendingItem.returnableItemId,
      );
      if (!returnableItem) {
        return total;
      }
      return total.plus(
        getReturnableItemTotalReturnValue(returnableItem, pendingItem.quantity),
      );
    },
    Big(0),
  );

  const usdToPresentmentRate = Big(
    draftReturn.currentUsdToPresentmentExchangeRate ?? 1,
  );
  const selectedItemsValueUsd =
    selectedItemsValuePresentment.div(usdToPresentmentRate);

  return selectedItemsValueUsd.gte(CLAIM_DOCUMENTS_VALUE_THRESHOLD_USD);
}
